import tenantTheme from '@theme';
import styled from 'styled-components';

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
  gap: 10px;
  ${(props) =>
    props.$rail &&
    `
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: 157px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    margin-inline: -16px;
    padding-inline: 16px;
    scroll-padding-inline: 16px;
    &::-webkit-scrollbar {
      display: none;
    }
    && > * {
      scroll-snap-align: start;
      min-height: 150px;
    }
  `}
`;

export const ProductTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 180px;
  padding: 10px;
  border: 1px solid ${tenantTheme['border-color-light']};
  border-radius: ${tenantTheme['card-radius']};
  background: linear-gradient(
    180deg,
    var(--tile-tint, ${tenantTheme['bg-color-light']}) 0%,
    ${tenantTheme['white-color']} 56px
  );

  &.is-click {
    cursor: pointer;
  }
  &.is-click:hover,
  &.is-click:focus-visible {
    border-color: ${tenantTheme['primary-color']};
    outline: none;
  }

  .tile-head {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .tile-name {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
    b {
      font-size: 13px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    small {
      font-size: 11px;
      color: ${tenantTheme['text-color-secondary']};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .tile-head {
    min-height: 34px;
  }
  /* the name sits at the top, the number, bar and note at the bottom */
  .tile-amount {
    margin-top: auto;
    line-height: 1.1;
    b {
      display: block;
      font-size: 24px;
      font-weight: 700;
    }
    span {
      font-size: 11px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .tile-meta {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    font-size: 11px;
    font-weight: 600;
    color: ${tenantTheme['text-color-secondary']};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Stacked bar: segments are flex-grown by their value; the rest of the track stays grey.
export const StackBar = styled.div`
  display: flex;
  height: ${(props) => props.$height || 6}px;
  border-radius: 99px;
  overflow: hidden;
  background: ${tenantTheme['bg-color-normal']};
  > i {
    display: block;
    height: 100%;
  }
`;

export const Swatch = styled.i`
  display: inline-block;
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(props) => props.$color};
`;

// Pill with an icon and a bold count: $small in dense lists, $button (as="button") for tappable chips.
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 0;
  border-radius: 99px;
  background: ${tenantTheme['bg-color-light']};
  font: inherit;
  font-size: 12px;
  color: ${tenantTheme['text-color-secondary']};
  white-space: nowrap;
  b {
    color: ${tenantTheme['text-color']};
  }
  ${(props) =>
    props.$small &&
    `
    gap: 4px;
    padding: 2px 8px;
    font-size: 11px;
  `}
  ${(props) =>
    props.$button &&
    `
    padding: 6px 10px;
    border-radius: ${tenantTheme['card-radius']};
    white-space: normal;
    cursor: pointer;
  `}
`;

// Agency pool split (not assigned / with agents / used): a swatch and label over each count.
export const PoolLegend = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  font-size: 12px;
  color: ${tenantTheme['text-color-secondary']};
  span {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    column-gap: 6px;
  }
  b {
    grid-column: 1 / -1;
    font-size: 15px;
    color: ${tenantTheme['text-color']};
  }
`;

export const DrawerSection = styled.div`
  .section-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 16px 12px 8px;
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
    b {
      font-weight: 700;
      color: ${tenantTheme['text-color']};
    }
  }
`;

// 64px rows, 2px apart, no dividers.
export const DrawerProductRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px minmax(0, 1.35fr);
  align-items: center;
  gap: 22px;
  min-height: 64px;
  padding: 14px 12px;
  margin-bottom: 2px;
  border-radius: 14px;
  scroll-margin-top: 8px;

  &.is-focus {
    background: ${tenantTheme['primary-light-4']};
  }

  /* phones: name and number on the first line, the bar and legend across the full width below */
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px 12px;
    padding: 12px 10px;
    > div:last-child {
      grid-column: 1 / -1;
    }
  }

  .row-name {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    b {
      display: block;
      font-size: 14px;
      line-height: 20px;
    }
    small {
      display: block;
      margin-top: 3px;
      font-size: 12px;
      line-height: 17px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .row-icon {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: 36px;
    height: 36px;
  }
  .row-amount {
    text-align: end;
    b {
      display: block;
      font-size: 18px;
      line-height: 22px;
    }
    span {
      font-size: 12px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .row-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 6px;
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
  }
`;

export const PackageBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${tenantTheme['card-radius']};
  background: ${tenantTheme['primary-light-4']};
  .banner-text {
    flex: 1;
    min-width: 0;
    b {
      display: block;
    }
    small {
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .banner-days {
    padding: 2px 10px;
    border-radius: 99px;
    background: ${tenantTheme['white-color']};
    border: 1px solid ${tenantTheme['border-color-light']};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
`;

// Packages page header: "Your package" and "Left to use".
export const PackageSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$mobile ? 'minmax(0, 1fr)' : '290px minmax(0, 1fr)')};
  gap: 16px;
  align-items: stretch;
`;

export const YourPackageTile = styled.div`
  display: grid;
  align-content: space-between;
  gap: 16px;
  min-height: 132px;
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(
    135deg,
    ${tenantTheme['white-color']} 0%,
    ${tenantTheme['primary-light-4']} 55%,
    ${tenantTheme['primary-light-3']} 100%
  );
  .pkg-badge {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${tenantTheme['white-color']};
  }
  .pkg-label {
    font-size: 12px;
    font-weight: 700;
    color: ${tenantTheme['primary-color']};
  }
  .pkg-name {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    small {
      margin-inline-start: 6px;
      font-size: 13px;
      font-weight: 600;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .pkg-dates {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
    b {
      color: ${tenantTheme['text-color']};
    }
  }
`;

export const LeftToUseTiles = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(128px, 1fr))'};
  gap: 10px;
  /* mobile: two columns, Add-ons across the full width */
  ${(props) =>
    props.$mobile &&
    `
    > [data-others] {
      grid-column: 1 / -1;
    }
  `}
`;

export const LeftToUseTile = styled.div`
  display: grid;
  align-content: start;
  gap: 6px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${tenantTheme['bg-color-light']};
  border: 1px solid transparent;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    border-color: ${tenantTheme['primary-color']};
    outline: none;
  }
  .ltu-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    min-height: 28px;
    font-size: 13px;
    font-weight: 600;
    line-height: 14px;
  }
  .ltu-icon {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: ${tenantTheme['border-radius-lg']};
    background: ${tenantTheme['white-color']};
  }
  .ltu-amount {
    line-height: 24px;
    font-size: 13px;
    color: ${tenantTheme['text-color-secondary']};
    b {
      font-size: 18px;
      color: ${tenantTheme['text-color']};
    }
  }
`;

// Agency Staff header: the agency on the left, its products on the right, in one card.
export const AgencyHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$mobile ? 'minmax(0, 1fr)' : 'minmax(240px, 300px) minmax(0, 1fr)')};
  gap: 24px;
  align-items: center;
`;

export const AgencyBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
  .agency-avatar {
    flex: none;
    width: 56px;
    height: 56px;
    line-height: 54px;
    border-radius: 16px;
    background: ${tenantTheme['primary-light-4']};
    border: 1px solid ${tenantTheme['primary-light-3']};
    color: ${tenantTheme['primary-color']};
    font-size: 18px;
    font-weight: 700;
  }
  .agency-text {
    min-width: 0;
  }
  /* the package chip stays inside the agency column: it wraps instead of running into the product tiles */
  .agency-package {
    display: inline-flex;
    flex-wrap: wrap;
    max-width: 100%;
    white-space: normal;
    border-radius: 12px;
    row-gap: 2px;
  }
  .agency-name {
    display: block;
    font-size: 20px;
    line-height: 1.25;
    word-break: break-word;
  }
  .agency-meta {
    margin-top: 4px;
    font-size: 13px;
    color: ${tenantTheme['text-color-secondary']};
    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      word-break: break-all;
    }
  }
`;

// Agency Staff on mobile: the pool summary under the agency.
export const MobilePool = styled.div`
  display: grid;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid ${tenantTheme['border-color-light']};
  .pool-cap {
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
  }
  .pool-big {
    font-size: 13px;
    color: ${tenantTheme['text-color-secondary']};
    b {
      font-size: 24px;
      color: ${tenantTheme['text-color']};
    }
  }
  .pool-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;
